import type { LucideIcon } from 'lucide-react';
import { Plus } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '@/components/atoms/button';

interface SectionEmptyStateProps {
  icon: LucideIcon;
  title: string;
  description: string;
  ctaLabel?: string;
  onCtaClick?: () => void;
  hideCta?: boolean;
  cta?: ReactNode;
  /**
   * `card` (default) — self-framed box (`rounded-lg border bg-background`) with an
   * outline CTA. Used by the Job-detail sections / notes / custom fields that
   * render the empty state standalone and rely on it for the box chrome.
   * `bare` — no border/background of its own (it lives inside a `DetailCard`),
   * a taller centered layout with a soft circle icon and a primary CTA. Used by
   * the Contact page (NOT-128).
   */
  variant?: 'card' | 'bare';
}

/**
 * Shared empty-state for detail sections (financials, jobs, contacts, …):
 * centered icon + a single title line + a single description line + an optional
 * CTA (a "Plus" button, or a custom `cta` node). Pass `hideCta` for sections
 * with no direct create action.
 */
export function SectionEmptyState({
  icon: Icon,
  title,
  description,
  ctaLabel,
  onCtaClick,
  hideCta = false,
  cta,
  variant = 'card',
}: SectionEmptyStateProps) {
  if (variant === 'bare') {
    return (
      <div className="flex min-h-[200px] flex-col items-center justify-center gap-1 px-6 py-10 text-center">
        <span className="mb-3 flex size-11 items-center justify-center rounded-full bg-muted">
          <Icon className="size-5 text-muted-foreground" />
        </span>
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="max-w-xs text-sm text-muted-foreground">{description}</p>
        {!hideCta && cta ? <div className="mt-4">{cta}</div> : null}
        {!hideCta && !cta && ctaLabel && onCtaClick ? (
          <Button type="button" variant="default" size="sm" className="mt-4" onClick={onCtaClick}>
            <Plus className="size-4" />
            {ctaLabel}
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-background px-4 py-8 text-center">
      <Icon className="mx-auto size-8 text-muted-foreground" />
      <p className="mt-3 text-sm font-medium text-foreground">{title}</p>
      <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      {!hideCta && cta ? <div className="mt-4">{cta}</div> : null}
      {!hideCta && !cta && ctaLabel && onCtaClick ? (
        <Button type="button" variant="outline" size="sm" className="mt-4" onClick={onCtaClick}>
          <Plus className="size-4" />
          {ctaLabel}
        </Button>
      ) : null}
    </div>
  );
}
